import React, { useState } from 'react';

const PLACEHOLDER_PATTERN = /via\.placeholder\.com|placehold\.co|placeholder\.com/i;

/** Returns true if the URL is a known placeholder or empty */
const isBadImage = (url) => !url || PLACEHOLDER_PATTERN.test(url);

/** Pick image src — first valid image from images[] array, or image string */
export const resolveImage = (property) => {
  const imgs = property?.images;
  if (Array.isArray(imgs)) {
    const found = imgs.find(i => !isBadImage(i));
    if (found) return found;
  }
  if (!isBadImage(property?.image)) return property?.image;
  return null;
};

/** Initials from a name string: "Godrej Elements" → "GE" */
const getInitials = (name = '') =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(w => w[0]?.toUpperCase() || '')
    .join('');

const GRADIENT_PAIRS = [
  ['#e53e3e', '#c53030'], // red
  ['#3182ce', '#2b6cb0'], // blue
  ['#38a169', '#276749'], // green
  ['#d69e2e', '#b7791f'], // yellow
  ['#805ad5', '#6b46c1'], // purple
  ['#dd6b20', '#c05621'], // orange
  ['#319795', '#2c7a7b'], // teal
  ['#d53f8c', '#b83280'], // pink
];

const pickGradient = (name = '') => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return GRADIENT_PAIRS[Math.abs(hash) % GRADIENT_PAIRS.length];
};

/**
 * PropertyImage — renders a property card image.
 * If no real image exists (or it's a placeholder/broken),
 * shows a coloured gradient tile with the project name initials.
 *
 * Props:
 *   property   — listing object (has .image, .images[], .name)
 *   className  — Tailwind classes for the wrapper div (e.g. "w-full h-52")
 *   imgClassName — extra classes for the <img> element
 */
const PropertyImage = ({ property, className = 'w-full h-52', imgClassName = 'w-full h-full object-cover hover:scale-105 transition-transform duration-500' }) => {
  const [broken, setBroken] = useState(false);
  const src = resolveImage(property);
  const name = property?.name || property?.title || '';
  const [c1, c2] = pickGradient(name);
  const initials = getInitials(name);

  if (!src || broken) {
    return (
      <div
        className={`${className} flex items-center justify-center`}
        style={{ background: `linear-gradient(135deg, ${c1}, ${c2})` }}
      >
        <span className="text-white text-4xl font-bold select-none tracking-widest opacity-80">
          {initials}
        </span>
      </div>
    );
  }

  return (
    <div className={className}>
      <img
        src={src}
        alt={name}
        className={imgClassName}
        onError={() => setBroken(true)}
      />
    </div>
  );
};

export default PropertyImage;
